import type { CoordeSeasonCapacity, Season } from '@cortex/domain-ac-mens-hub-api';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCoordeCapacity } from '../hooks/useCoordeCapacity.js';
import { useCoordePreview, useRequestCoordeRun } from '../hooks/useCoordeRun.js';

/**
 * コーデ生成の依頼。
 *
 * 画像生成は従量課金なので、**押す前に「何が作られるか」と「何本まで作れるか」を見せる**。
 * 依頼画面と結果画面を分けているのはそのためで、ここでは費用が発生しない。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Styling, Marketing
 * @graph-business Lets someone request outfit generation after seeing the ceiling and the exact garments that would be used, so nothing is paid for blind.
 * @graph-connects useCoordeCapacity [calls] 季節ごとの生成上限取得
 * @graph-connects useCoordePreview [calls] コーデ案の事前確認
 * @graph-connects useRequestCoordeRun [calls] 生成の依頼
 */

/**
 * 季節の表示順。phoenix-config constants.json の SUB_SEASON と同じ並び。
 * @graph-connects none
 */
const SEASON_ORDER: readonly Season[] = [
  'earlySpring',
  'lateSpring',
  'summer',
  'earlyAutumn',
  'lateAutumn',
  'winter',
] as const;

/**
 * 依頼できる本数の上限を決める。
 *
 * その季節の在庫上限を超える本数を頼んでも、組めずに減った本数しか返ってこない。
 * 頼む前に上限で頭打ちにして、「10本頼んだのに3本しか来ない」を画面の側で防ぐ。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling, Marketing
 * @graph-business Caps the request at what the season's stock can actually produce, so the requester is not surprised by a short run.
 * @graph-connects none
 */
export function resolveMaxCount(seasons: readonly CoordeSeasonCapacity[], season: Season): number {
  return seasons.find((s) => s.season === season)?.maxOutfits ?? 0;
}

/**
 * 入力された本数を実際に頼める範囲に丸める。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling, Marketing
 * @graph-business Keeps the requested count inside what the season allows, including the case where nothing can be generated at all.
 * @graph-connects none
 */
export function clampCount(count: number, maxCount: number): number {
  if (maxCount <= 0) return 0;
  return Math.min(Math.max(1, Math.floor(count)), maxCount);
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling, Marketing
 * @graph-business The screen where marketing asks for outfit images: it shows the season's ceiling, previews the exact garments for free, and only then starts the paid generation.
 * @graph-connects useCoordePreview [calls] コーデ案の事前確認
 * @graph-connects useRequestCoordeRun [calls] 生成の依頼
 */
export function CoordeRequestPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: capacity } = useCoordeCapacity();
  const preview = useCoordePreview();
  const requestRun = useRequestCoordeRun();

  const [season, setSeason] = useState<Season>('earlyAutumn');
  const [count, setCount] = useState(5);
  const [includeOuter, setIncludeOuter] = useState(false);

  const seasons = capacity?.seasons ?? [];
  const maxCount = useMemo(() => resolveMaxCount(seasons, season), [seasons, season]);
  const effectiveCount = clampCount(count, maxCount);
  const canRequest = effectiveCount > 0 && !requestRun.isPending;

  const input = { season, count: effectiveCount, includeOuter };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[20px] font-semibold tracking-tight text-[var(--color-text-primary)]">
          {t('coordeRequest.title')}
        </h1>
        <p className="mt-1 text-[13px] text-[var(--color-text-muted)]">
          {t('coordeRequest.description')}
        </p>
      </div>

      <div className="space-y-4 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-4">
        <label className="block space-y-1">
          <span className="text-[12px] text-[var(--color-text-muted)]">
            {t('coordeRequest.season')}
          </span>
          <select
            value={season}
            onChange={(e) => {
              setSeason(e.target.value as Season);
            }}
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-[13px] text-[var(--color-text-primary)]"
          >
            {SEASON_ORDER.map((value) => (
              <option key={value} value={value}>
                {t(`itemCatalog.season.${value}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="block space-y-1">
          <span className="text-[12px] text-[var(--color-text-muted)]">
            {t('coordeRequest.count', { max: maxCount })}
          </span>
          <input
            type="number"
            min={1}
            max={maxCount}
            value={count}
            onChange={(e) => {
              setCount(Number(e.target.value));
            }}
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-[13px] tabular-nums text-[var(--color-text-primary)]"
          />
        </label>

        <label className="flex items-center gap-2 text-[13px] text-[var(--color-text-primary)]">
          <input
            type="checkbox"
            checked={includeOuter}
            onChange={(e) => {
              setIncludeOuter(e.target.checked);
            }}
          />
          {t('coordeRequest.includeOuter')}
        </label>

        {maxCount <= 0 ? (
          <p className="text-[13px] text-[var(--color-error)]">{t('coordeRequest.noStock')}</p>
        ) : null}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              preview.mutate(input);
            }}
            disabled={!(effectiveCount > 0) || preview.isPending}
            className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-[13px] text-[var(--color-text-muted)] disabled:opacity-50"
          >
            {t('coordeRequest.preview')}
          </button>
          <button
            type="button"
            onClick={() => {
              requestRun.mutate(input, {
                onSuccess: (accepted) => {
                  void navigate({
                    to: '/coorde/$runId',
                    params: { runId: accepted.runId },
                  });
                },
              });
            }}
            disabled={!canRequest}
            className="rounded-md border border-[var(--color-accent-primary)] px-3 py-1.5 text-[13px] text-[var(--color-text-primary)] disabled:opacity-50"
          >
            {t('coordeRequest.submit', { count: effectiveCount })}
          </button>
        </div>

        {preview.isError ? (
          <p role="alert" className="text-[13px] text-[var(--color-error)]">
            {t('coordeRequest.previewError')}:{' '}
            {preview.error instanceof Error ? preview.error.message : t('common.unknownError')}
          </p>
        ) : null}

        {requestRun.isError ? (
          <p role="alert" className="text-[13px] text-[var(--color-error)]">
            {t('coordeRequest.submitError')}:{' '}
            {requestRun.error instanceof Error
              ? requestRun.error.message
              : t('common.unknownError')}
          </p>
        ) : null}
      </div>

      {preview.data ? (
        <div className="space-y-2">
          <h2 className="text-[15px] font-semibold text-[var(--color-text-primary)]">
            {t('coordeRequest.previewResult', { count: preview.data.outfits.length })}
          </h2>
          <ul className="divide-y divide-[var(--color-border)] overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]">
            {preview.data.outfits.map((outfit) => (
              <li key={outfit.index} className="px-4 py-2 text-[13px]">
                <span className="mr-2 tabular-nums text-[var(--color-text-muted)]">
                  {outfit.index}
                </span>
                <span className="text-[var(--color-text-primary)]">
                  {outfit.items.map((item) => `${item.brandName} ${item.itemName}`).join(' / ')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <p className="text-[12px] text-[var(--color-text-muted)]">{t('coordeRequest.note')}</p>
    </div>
  );
}
